import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';

export function createRaysShape(vertices: any[], colors: any[], opacity = 0.9, linewidth = 2) {
    const lineSegmentsGeometry = new LineSegmentsGeometry();
    lineSegmentsGeometry.setPositions(vertices);
    lineSegmentsGeometry.setColors(colors);

    const lineMaterial = new LineMaterial({
        transparent: true,
        opacity: opacity,
        vertexColors: true,
        linewidth: linewidth,
        resolution: new THREE.Vector2(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio),
    });
    return new LineSegments2(lineSegmentsGeometry, lineMaterial);
}

export function create2DPointsShape(vertices: any[] | any, pointSize = 4, attenuation = true, color = 0x000000) {
    const texture = createCircleTexture(128, color);
    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    const material = new THREE.PointsMaterial({
        color: color,
        size: pointSize,
        sizeAttenuation: attenuation,
        map: texture,
        transparent: true, // Important to handle the texture's transparency
    });

    return new THREE.Points(pointsGeometry, material);
}

/**
 *
 * @deprecated Use specific methods instead: createThickLine if width !== 1 or createThinLine if width === 1
 * @param vertices
 * @param colors
 * @param width
 * @returns
 */
export function createMultiLineShape(vertices: any, colors: any[], width = 2): LineSegments2 | THREE.Line {
    if (vertices.length == 0) return new LineSegments2();
    if (width !== 1) {
        return createThickLine(vertices, colors, width);
    } else {
        return createThinLine(vertices, colors);
    }
}

export function createThickLine(vertices: any, colors: any[], width = 2): LineSegments2 {
    let lineMaterial, lineGeometry;
    if (vertices.length == 0) return new LineSegments2();
    lineGeometry = new LineGeometry();
    lineGeometry.setPositions(vertices);
    lineGeometry.setColors(colors);

    lineMaterial = new LineMaterial({
        transparent: false,
        opacity: 0.9,
        vertexColors: true,
        linewidth: width, // only this property is dependent on width
        resolution: new THREE.Vector2(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio),
    });

    return new LineSegments2(lineGeometry, lineMaterial);
}

export function createThinLine(vertices: any, colors: any[]): THREE.Line {
    let lineMaterial, lineGeometry;
    if (vertices.length == 0) return new THREE.Line();

    lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    lineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    lineMaterial = new THREE.LineBasicMaterial({
        transparent: true,
        opacity: 0.7,
        vertexColors: true,
    });

    return new THREE.Line(lineGeometry, lineMaterial);
}

/**
 * Generates a polyline from 3D points.
 *
 * @param {Array<THREE.Vector3>} points - Array of 3D points (THREE.Vector3) representing polyline vertices.
 * @returns {THREE.Mesh} - Polyline mesh created from the points.
 *
 * @description
 * Converts an array of points into vertex positions, assigns default colors, and returns a polyline mesh using `createMultiLineShape()`.
 */
export function generatePolyLine(points: any[]) {
    // Convert points into array of vertices:
    const positions: any[] = [];
    points.forEach((point) => {
        positions.push(point.x, point.y, point.z);
    });
    const vertices = new Float32Array(positions);
    let colors = Array(vertices.length).fill(0);
    return createMultiLineShape(vertices, colors);
}

export function create3DPointsShape(vertices: any, sphereRadius = 5) {
    // if (vertices.length % 3 !== 0) {
    //   throw new Error('Vertices array length should be a multiple of 3.');
    // }

    const sphereGeometry = new THREE.SphereGeometry(sphereRadius, 32, 32);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });

    const instanceCount = vertices.length;
    const instancedMesh = new THREE.InstancedMesh(sphereGeometry, sphereMaterial, instanceCount);
    // const matrix = new THREE.Matrix4();
    // const position = new THREE.Vector3();

    // for (let i = 0; i < instanceCount; i++) {
    //   // Extract the position from the flat array.
    //   position.set(vertices[i * 3], vertices[i * 3 + 1], vertices[i * 3 + 2]);

    //   // Set the position in the matrix and apply it to the instance.
    //   matrix.setPosition(vertices);
    //   instancedMesh.setMatrixAt(i, matrix);
    // }

    // Update the instance matrix data.
    // instancedMesh.instanceMatrix.needsUpdate = true;

    return instancedMesh;
}

export function createCircleTexture(radius = 128, colorValue: number) {
    // Create a canvas
    const canvas = document.createElement('canvas');
    const context: CanvasRenderingContext2D =
        canvas.getContext('2d') ||
        (() => {
            throw new Error('Failed to get canvas context');
        })();

    // Set canvas dimensions
    canvas.width = canvas.height = radius * 2;

    const color = new THREE.Color(colorValue);

    context.fillStyle = color.getStyle(); // Use the passed color

    // Draw a circle
    context.beginPath();
    context.arc(radius, radius, radius, 0, 2 * Math.PI, false);
    context.fill();

    // Create a texture
    const texture = new THREE.Texture(canvas);
    texture.flipY = false; // Set flipY to false to maintain the top-to-bottom orientation

    texture.needsUpdate = true;

    return texture;
}
